import { Button } from '@/components/ui';
import { BrandMark } from '@/components/BrandMark';
import { useState } from 'react';
import { useAuth, type AuthStatus } from '@/auth/auth';
import { rememberReturnPath } from '@/auth/sessionLoss';
import { clearIapSession } from '@/auth/authChannel';
import { DEMO, LOGIN_URL } from '@/api/config';

/**
 * Every non-success auth state, tersely: what happened, what is known,
 * what to do next. No raw OAuth errors, ever.
 */
const states: Partial<Record<AuthStatus, { title: string; body: string; action: string }>> = {
  'signed-out': {
    title: 'Sign in to Poseidon',
    body: 'Reconstruction intelligence for company data exports.',
    action: 'Continue with Google',
  },
  'unauthorized-domain': {
    title: 'Account not permitted',
    body: 'Poseidon is limited to withmeridian.ai workspace accounts. Switch accounts and try again.',
    action: 'Use a different account',
  },
  'unverified-email': {
    title: 'Email not verified',
    body: 'Your Google account email is unverified. Verify it in Google, then sign in again.',
    action: 'Try again',
  },
  expired: {
    title: 'Session expired',
    body: 'Your session timed out. Sign in to continue where you left off.',
    action: 'Sign in again',
  },
  revoked: {
    title: 'Session revoked',
    body: 'An administrator ended this session. Sign in to start a new one.',
    action: 'Sign in',
  },
  /**
   * The one refusal the live app can actually reach, covering three people:
   * an account that is not enrolled, one on the wrong domain, and one whose
   * role is too low. The old copy sent all three to an administrator to be
   * enrolled, which is the wrong instruction for two of them, and it stated
   * "not enrolled" as though the app knew that. It names both moves instead,
   * and the API's own sentence appears beneath when it says which.
   */
  'insufficient-role': {
    title: 'This account cannot open Poseidon',
    body: 'Either it is not enrolled, or it is outside the withmeridian.ai workspace, or its role does not reach this app. Switching to an enrolled withmeridian.ai account fixes the first two; the third needs an administrator.',
    action: 'Switch account',
  },
  'auth-unavailable': {
    title: 'Sign-in is unavailable',
    body: 'The authentication service is not responding. Nothing is wrong with your account. Try again shortly.',
    action: 'Try signing in again',
  },
};

/** States that hold a live IAP session for the WRONG/blocked account: the exit
 * is clearing that session and re-entering through Google's account chooser. */
const BLOCKED: AuthStatus[] = ['insufficient-role', 'unauthorized-domain', 'unverified-email', 'expired', 'revoked'];

export function SignIn() {
  const { status, signIn, signOut, demo, blockedReason } = useAuth();
  const s = states[status] ?? states['signed-out']!;
  const busy = status === 'signing-in' || status === 'loading';
  const blocked = BLOCKED.includes(status);

  const [switching, setSwitching] = useState(false);
  const switchAccount = async () => {
    if (DEMO) {
      signOut();
      return;
    }
    // This used to broadcast signed-out first, which this tab receives, so
    // for 400ms the operator watched "this account cannot open Poseidon"
    // become "sign in to Poseidon", a different and untrue statement, and
    // then the page navigated. It also guessed at 400ms with no busy state,
    // so a second click fired a second clear and a second navigation, and if
    // the clear had not landed IAP re-admitted the same refused account.
    setSwitching(true);
    rememberReturnPath(`${window.location.pathname}${window.location.search}`);
    await clearIapSession();
    window.location.assign(LOGIN_URL);
  };

  return (
    <main className="grid h-screen place-items-center bg-bg">
      <div className="flex w-[340px] flex-col items-center text-center">
        <BrandMark size={44} />
        <h1 className="mt-5 text-h2 text-ink">{busy ? (status === 'loading' ? 'Checking session' : 'Signing in') : s.title}</h1>
        <p className="mt-1.5 text-small text-ink-2">{busy ? 'Waiting for Google.' : s.body}</p>
        {!busy && blockedReason && (
          <p className="mt-2 rounded border border-line bg-panel px-3 py-2 text-caption text-ink-3">{blockedReason}</p>
        )}
        <Button
          className="mt-6 w-full"
          loading={busy || switching}
          disabled={switching}
          onClick={blocked ? () => void switchAccount() : signIn}
        >
          {s.action}
        </Button>
        {blocked && (
          <Button variant="ghost" className="mt-2 w-full" onClick={signIn}>
            Try this account again
          </Button>
        )}
        <p className="mt-4 text-caption text-ink-3">
          withmeridian.ai accounts only · sessions are server managed
        </p>
        <p className="mt-8 text-caption text-ink-4">
          Poseidon · Meridian International{demo && <span className="mono"> · demo data</span>}
        </p>
      </div>
    </main>
  );
}
